import IOfferGet from 'api/interfaces/offers/IOfferGet';
import IOfferGetSingle from 'api/interfaces/offers/IOfferGetSingle';
import api from './api';
import IOfferProductPost from './interfaces/offers/IOfferProductPost';
import IOfferProductGet from './interfaces/offers/IOfferProductGet';
import IOfferProductsGet from './interfaces/offers/IOfferProductsGet';
import IIdResult from './interfaces/results/IIdResult';
import ISuccessResult from './interfaces/results/ISuccessResult';
import IAdditionalOfferGet from './interfaces/offers/IAdditionalOfferGet';
import IOfferProductImage from './interfaces/offers/IOfferProductImage';
import { IOfferNdaPut } from './interfaces/offers/IOfferNda';
import IOfferRevision from './interfaces/offers/IOfferRevision';
import IOfferTextTemplate from './interfaces/offers/IOfferTextTemplate';
import ISearchedPosition from './interfaces/offers/ISearchedPosition';
import IPdfLayoutPosition from './interfaces/offers/IPdfLayoutPosition';
import IRestorablePosition from './interfaces/offers/IRestorablePosition';

export default {
  additionalOffers: {
    delete: (id: number): Promise<ISuccessResult> =>
      api.delete(`/api/v1/additionaloffers/${id}`),
    get: async (id?: number): Promise<IAdditionalOfferGet> =>
      api.get(`/api/v1/additionaloffers/${id}`),
    pdfs: {
      get: async (id?: number): Promise<any> =>
        api.getPdf(`/api/v1/additionaloffers/${id}/pdf`),
    },
    post: async (offer?: any): Promise<any> =>
      api.post('/api/v1/additionaloffers', offer),
    postCopyAdditionalOffer: async (id?: number): Promise<any> =>
      api.post(`/api/v1/copyadditionaloffer/${id}`),
    put: async (offer?: any, id?: number): Promise<any> =>
      api.put(`/api/v1/additionaloffers/${id}`, offer),
    patchStatus: (id: number, status: string): Promise<IIdResult> =>
      api.patch(`/api/v1/additionaloffers/${id}/status`, { status }),
    patchMemo: (id: number, memo: string): Promise<IIdResult> =>
      api.patch(`/api/v1/additionaloffers/${id}/memo`, { memo }),
    copiedPositions: {
      post: (id: number, offerProductId: number): Promise<IIdResult> =>
        api.post(`/api/v1/additionaloffers/${id}/copied-positions`, {
          offerProductId,
        }),
    },
    pdfLayout: {
      get: (id: number): Promise<IPdfLayoutPosition[]> =>
        api.get(`/api/v1/additionaloffers/${id}/pdf-layout`),
    },
    revisions: {
      get: (id: number): Promise<IOfferRevision[]> =>
        api.get(`/api/v1/additionaloffers/${id}/revisions`),
      post: (id: number): Promise<IIdResult> =>
        api.post(`/api/v1/additionaloffers/${id}/revisions`),
    },
    restorablePositions: {
      get: (id: number): Promise<IRestorablePosition[]> =>
        api.get(`/api/v1/additionaloffers/${id}/restorable-positions`),
      restore: (id: number, offerProductId: number): Promise<IIdResult> =>
        api.post(`/api/v1/additionaloffers/${id}/restored-positions`, {
          offerProductId,
        }),
    },
  },
  count: async (filters: {
    created?: string;
    createdUserId?: string;
    customer?: string;
    recipient?: string;
    search?: string;
    orderBy?: string;
    orderByDirection?: string;
    projectId?: number;
    updated?: string;
    updatedUserId?: string;
    contactId?: string;
    type?: string;
    status?: string;
  }): Promise<any> => api.get('/api/v1/offers/count', filters),
  get: async (filters: {
    created?: string;
    createdUserId?: string;
    customer?: string;
    recipient?: string;
    limit?: number;
    offset?: number;
    search?: string;
    orderBy?: string;
    orderByDirection?: string;
    projectId?: number;
    updated?: string;
    updatedUserId?: string;
    contactId?: string;
    type?: string;
    status?: string;
  }): Promise<IOfferGet[]> => api.get('/api/v1/offers', filters),
  getOffer: async (id?: number): Promise<IOfferGetSingle> =>
    api.get(`/api/v1/offers/${id}`),
  offerTypes: {
    get: async (): Promise<string[]> => api.get('/api/v1/offers/types'),
  },
  pdfs: {
    // contactId prints the version of the offer for one customer company
    get: async (id?: number, contactId?: number): Promise<any> =>
      api.getPdf(
        `/api/v1/offers/${id}/pdf`,
        contactId === undefined ? undefined : { contactId }
      ),
  },
  post: async (offer?: any): Promise<any> => api.post('/api/v1/offers', offer),
  postCopyOffer: async (id?: number): Promise<any> =>
    api.post(`/api/v1/copyoffer/${id}`),
  delete: (id: number): Promise<ISuccessResult> =>
    api.delete(`/api/v1/offers/${id}`),
  patchStatus: (id: number, status: string): Promise<IIdResult> =>
    api.patch(`/api/v1/offers/${id}/status`, { status }),
  patchMemo: (id: number, memo: string): Promise<IIdResult> =>
    api.patch(`/api/v1/offers/${id}/memo`, { memo }),
  // NDA protected parts and users, shared by all revisions and additional offers
  putNda: (id: number, nda: IOfferNdaPut): Promise<IIdResult> =>
    api.put(`/api/v1/offers/${id}/nda`, nda),
  // Positions of earlier offers whose content can be copied to a new position
  positionSearch: {
    get: (filters: {
      group?: string;
      search?: string;
      status?: string;
      type?: string;
    }): Promise<ISearchedPosition[]> =>
      api.get('/api/v1/offers/position-search', filters),
  },
  copiedPositions: {
    post: (id: number, offerProductId: number): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${id}/copied-positions`, { offerProductId }),
  },
  // Archive of reusable offer texts
  textTemplates: {
    delete: (id: number): Promise<ISuccessResult> =>
      api.delete(`/api/v1/offers/text-templates/${id}`),
    get: (search?: string): Promise<IOfferTextTemplate[]> =>
      api.get('/api/v1/offers/text-templates', search ? { search } : undefined),
    post: (name: string, text: string): Promise<IIdResult> =>
      api.post('/api/v1/offers/text-templates', { name, text }),
  },
  pdfLayout: {
    get: (id: number): Promise<IPdfLayoutPosition[]> =>
      api.get(`/api/v1/offers/${id}/pdf-layout`),
  },
  revisions: {
    get: (id: number): Promise<IOfferRevision[]> =>
      api.get(`/api/v1/offers/${id}/revisions`),
    post: (id: number): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${id}/revisions`),
  },
  restorablePositions: {
    get: (id: number): Promise<IRestorablePosition[]> =>
      api.get(`/api/v1/offers/${id}/restorable-positions`),
    restore: (id: number, offerProductId: number): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${id}/restored-positions`, { offerProductId }),
  },

  products: {
    delete: (id: number): Promise<ISuccessResult> =>
      api.delete(`/api/v1/offers/products/${id}`),
    patchPageBreak: (
      productId: number,
      pageBreakBefore: boolean
    ): Promise<IIdResult> =>
      api.patch(`/api/v1/offers/products/${productId}/page-break`, {
        pageBreakBefore,
      }),
    images: {
      delete: (productId: number, imageId: number): Promise<ISuccessResult> =>
        api.delete(`/api/v1/offers/products/${productId}/images/${imageId}`),
      get: (productId: number): Promise<IOfferProductImage[]> =>
        api.get(`/api/v1/offers/products/${productId}/images`),
      getFile: (productId: number, imageId: number): Promise<any> =>
        api.getFile(
          `/api/v1/offers/products/${productId}/images/${imageId}/file`
        ),
      patch: (
        productId: number,
        imageId: number,
        height: string
      ): Promise<IIdResult> =>
        api.patch(`/api/v1/offers/products/${productId}/images/${imageId}`, {
          height,
        }),
      post: (
        productId: number,
        fileId: string,
        height: string
      ): Promise<IIdResult> =>
        api.post(`/api/v1/offers/products/${productId}/images`, {
          fileId,
          height,
        }),
    },
    get: (id: number): Promise<IOfferProductGet> =>
      api.get(`/api/v1/offers/products/${id}`),
    getForAdditionalOffer: (offerId: number): Promise<IOfferProductsGet[]> =>
      api.get(`/api/v1/additionaloffers/${offerId}/products`),
    getForOffer: (offerId: number): Promise<IOfferProductsGet[]> =>
      api.get(`/api/v1/offers/${offerId}/products`),
    post: async (
      offerId: number,
      offerProduct: IOfferProductPost
    ): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${offerId}/products`, offerProduct),
    postAdditionalOffer: async (
      offerId: number,
      offerProduct: IOfferProductPost
    ): Promise<IIdResult> =>
      api.post(`/api/v1/additionaloffers/${offerId}/products`, offerProduct),
    put: async (
      id: number,
      offerProduct: IOfferProductPost
    ): Promise<IIdResult> =>
      api.put(`/api/v1/offers/products/${id}`, offerProduct),
    putAdditionalOffer: async (
      id: number,
      offerProduct: IOfferProductPost
    ): Promise<IIdResult> =>
      api.put(`/api/v1/offers/products/${id}`, offerProduct),
  },
  put: async (offer?: any, id?: number): Promise<any> =>
    api.put(`/api/v1/offers/${id}`, offer),
  status: {
    get: async (): Promise<string[]> => api.get('/api/v1/offers/types/status'),
  },
};
